import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  HiChevronLeft,
  HiChevronRight,
  HiEye,
  HiMenu,
  HiPlus,
  HiSearch,
  HiX,
} from 'react-icons/hi';
import { MdEdit } from 'react-icons/md';
import Sidebar from '../../../components/Sidebar';
import Toast from '../../../components/toaster';
import MeditationNav from '../MeditationNav';
import {
  createMeditationAchievement,
  getMeditationAchievement,
  getMeditationAchievementEnums,
  listMeditationAchievements,
  updateMeditationAchievement,
} from '../../../services/meditationAchievementsService';
import {
  buildAchievementListBody,
  formatTriggerConfig,
  summarizeTriggerConfig,
} from './achievementDisplayUtils';
import BadgeFormModal from './BadgeFormModal';

const PAGE_SIZE = 20;

const StatusPill = ({ active }) => (
  <span
    className={`inline-flex px-2 py-0.5 rounded-lg text-xs font-medium ${
      active
        ? 'bg-green-50 text-green-700 border border-green-200'
        : 'bg-gray-100 text-gray-600 border border-gray-200'
    }`}
  >
    {active ? 'Active' : 'Inactive'}
  </span>
);

const DeferredPill = () => (
  <span className="inline-flex px-2 py-0.5 rounded-lg text-xs font-semibold bg-amber-50 text-amber-800 border border-amber-200">
    Deferred
  </span>
);

const DetailRow = ({ label, children }) => (
  <div className="grid grid-cols-1 sm:grid-cols-3 gap-1 sm:gap-3 py-2 border-b border-gray-100 last:border-0">
    <dt className="text-xs font-medium text-gray-500">{label}</dt>
    <dd className="sm:col-span-2 text-sm text-gray-800 break-words">{children}</dd>
  </div>
);

const MeditationAchievements = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [enums, setEnums] = useState({
    categories: [],
    rarities: [],
    trigger_types: [],
  });

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [detailDoc, setDetailDoc] = useState(null);
  const detailRequestRef = useRef(0);

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [formDoc, setFormDoc] = useState(null);
  const [formSaving, setFormSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const formSavingRef = useRef(false);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(searchQuery.trim()), 400);
    return () => window.clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getMeditationAchievementEnums();
        if (cancelled) return;
        const data = res?.data || {};
        setEnums({
          categories: Array.isArray(data.categories) ? data.categories : [],
          rarities: Array.isArray(data.rarities) ? data.rarities : [],
          trigger_types: Array.isArray(data.trigger_types) ? data.trigger_types : [],
        });
      } catch (_err) {
        // Enums only needed for create/edit modal; list still works without them.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadList = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const body = buildAchievementListBody({
        page: currentPage,
        pageSize: PAGE_SIZE,
        searchtext: debouncedSearch,
        filters: {},
      });
      const res = await listMeditationAchievements(body);
      const list = Array.isArray(res?.data) ? res.data : [];
      const total = typeof res?.totalCount === 'number' ? res.totalCount : list.length;
      setRows(list);
      setTotalCount(total);
    } catch (err) {
      setRows([]);
      setTotalCount(0);
      setError(err.message || 'Failed to load meditation achievements');
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, debouncedSearch]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE) || 1);

  const changePage = (next) => {
    const page = typeof next === 'function' ? next(currentPage) : next;
    if (page < 1 || page > totalPages || page === currentPage) return;
    setCurrentPage(page);
  };

  const getPaginationRange = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const pages = [1];
    const left = Math.max(2, currentPage - 1);
    const right = Math.min(totalPages - 1, currentPage + 1);
    if (left > 2) pages.push('dots');
    for (let p = left; p <= right; p++) pages.push(p);
    if (right < totalPages - 1) pages.push('dots');
    pages.push(totalPages);
    return pages;
  };

  const openDetail = async (id) => {
    if (!id) return;
    const requestId = Date.now();
    detailRequestRef.current = requestId;
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailError('');
    setDetailDoc(null);
    try {
      const res = await getMeditationAchievement(id);
      if (detailRequestRef.current !== requestId) return;
      setDetailDoc(res?.data ?? null);
      if (!res?.data) {
        setDetailError('Meditation achievement not found');
      }
    } catch (err) {
      if (detailRequestRef.current !== requestId) return;
      setDetailError(err.message || 'Failed to load badge details');
    } finally {
      if (detailRequestRef.current === requestId) {
        setDetailLoading(false);
      }
    }
  };

  const closeDetail = () => {
    detailRequestRef.current += 1;
    setDetailOpen(false);
    setDetailDoc(null);
    setDetailError('');
    setDetailLoading(false);
  };

  const openCreate = () => {
    setFormMode('create');
    setFormDoc(null);
    setFormError('');
    setFormOpen(true);
  };

  const openEdit = (doc) => {
    if (!doc) return;
    setFormMode('edit');
    setFormDoc(doc);
    setFormError('');
    setFormOpen(true);
  };

  const handleFormSubmit = async (payload) => {
    if (formSavingRef.current || formSaving) return;
    formSavingRef.current = true;
    setFormSaving(true);
    setFormError('');
    try {
      if (formMode === 'create') {
        await createMeditationAchievement(payload);
        Toast.success('Meditation achievement created successfully');
      } else {
        await updateMeditationAchievement(payload);
        Toast.success('Meditation achievement updated successfully');
      }
      setFormOpen(false);
      setFormDoc(null);
      closeDetail();
      await loadList();
    } catch (err) {
      setFormError(err.message || 'Save failed');
      Toast.error(err.message || 'Save failed');
    } finally {
      formSavingRef.current = false;
      setFormSaving(false);
    }
  };

  const hasSearch = Boolean(debouncedSearch);

  return (
    <div className="h-screen flex overflow-hidden">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="flex-1 bg-primary flex flex-col px-4 py-4 md:p-6 overflow-hidden">
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          <div className="mb-4 md:mb-6">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 rounded-lg hover:bg-white/50 transition-colors"
                aria-label="Open menu"
              >
                <HiMenu className="w-6 h-6 text-gray-800" />
              </button>
              <div>
                <h1 className="text-xl md:text-3xl font-bold text-gray-800">Meditation</h1>
                <p className="text-sm text-gray-500 mt-0.5">
                  Achievements / Dhyaan Badges
                </p>
              </div>
            </div>
          </div>

          <MeditationNav />

          <div className="mb-4 md:mb-6 flex-shrink-0">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 min-w-0">
              <div className="w-full sm:max-w-xs relative flex-shrink-0">
                <HiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-450 w-4 h-4 pointer-events-none" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search name, code, description…"
                  className="w-full h-10 pl-9 pr-3 text-sm border border-[#ECECEC] rounded-xl focus:outline-none bg-white"
                  aria-label="Search meditation achievements"
                />
              </div>
              <div className="flex flex-wrap gap-2 flex-shrink-0">
                <button
                  type="button"
                  onClick={openCreate}
                  className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-semibold text-white bg-brand rounded-xl shadow-md hover:opacity-90"
                >
                  <HiPlus className="w-4 h-4" />
                  Create badge
                </button>
              </div>
            </div>
          </div>

          {isLoading ? (
            <div className="flex-1 flex items-center justify-center" role="status">
              <div className="flex items-center gap-3 text-sm text-gray-600">
                <svg
                  className="animate-spin h-5 w-5 text-brand"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Loading badges…
              </div>
            </div>
          ) : error ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center max-w-md">
                <p className="text-red-600 mb-3 whitespace-pre-line">{error}</p>
                <button
                  type="button"
                  onClick={loadList}
                  className="px-4 py-2 text-sm font-medium text-white bg-brand rounded-lg"
                >
                  Retry
                </button>
              </div>
            </div>
          ) : totalCount === 0 ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center max-w-md text-gray-500">
                <p className="text-sm font-medium text-gray-700 mb-1">No badges found</p>
                <p className="text-xs">
                  {hasSearch
                    ? 'Try clearing the search query.'
                    : 'No meditation achievements returned by the API yet.'}
                </p>
              </div>
            </div>
          ) : (
            <div className="flex-1 min-h-0 flex flex-col bg-white rounded-xl shadow-sm border border-[#ECECEC] overflow-hidden">
              <div className="px-4 md:px-6 py-3 border-b border-gray-100 text-xs text-gray-500 flex-shrink-0">
                Showing{' '}
                {`${(currentPage - 1) * PAGE_SIZE + 1}–${Math.min(
                  currentPage * PAGE_SIZE,
                  totalCount
                )}`}{' '}
                of {totalCount}
              </div>
              <div className="flex-1 min-h-0 overflow-auto">
                <table className="w-full min-w-[720px] lg:min-w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      <th className="px-3 md:px-4 py-3 whitespace-nowrap">#</th>
                      <th className="px-3 md:px-4 py-3">Logo</th>
                      <th className="px-3 md:px-4 py-3 min-w-[8rem]">Name</th>
                      <th className="px-3 md:px-4 py-3 hidden md:table-cell">Code</th>
                      <th className="px-3 md:px-4 py-3 hidden lg:table-cell">Category</th>
                      <th className="px-3 md:px-4 py-3 hidden sm:table-cell">Rarity</th>
                      <th className="px-3 md:px-4 py-3 hidden xl:table-cell">Trigger</th>
                      <th className="px-3 md:px-4 py-3">Status</th>
                      <th className="px-3 md:px-4 py-3 hidden xl:table-cell">Order</th>
                      <th className="px-3 md:px-4 py-3 hidden xl:table-cell">Progress</th>
                      <th className="px-3 md:px-4 py-3 text-right whitespace-nowrap">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {rows.map((row) => {
                      const deferred = row.deferred === true;
                      return (
                        <tr
                          key={row._id}
                          className={deferred ? 'bg-amber-50/40' : 'bg-white'}
                        >
                          <td className="px-3 md:px-4 py-3 font-semibold text-gray-800 tabular-nums whitespace-nowrap">
                            {row.badge_number ?? '—'}
                          </td>
                          <td className="px-3 md:px-4 py-3">
                            {row.logo_url ? (
                              <img
                                src={row.logo_url}
                                alt=""
                                className="h-10 w-10 rounded-lg object-cover border border-gray-200 bg-gray-50"
                              />
                            ) : (
                              <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-dashed border-gray-200 text-[10px] text-gray-400">
                                N/A
                              </span>
                            )}
                          </td>
                          <td className="px-3 md:px-4 py-3">
                            <div className="font-medium text-gray-900">{row.name || '—'}</div>
                            <div className="text-xs text-gray-500">{row.sanskrit_name || '—'}</div>
                            <div className="md:hidden mt-1 font-mono text-[11px] text-gray-500">
                              {row.code || '—'}
                            </div>
                            <div className="xl:hidden mt-0.5 text-[11px] text-gray-400">
                              {row.trigger_type || '—'}
                              {row.trigger_config
                                ? ` · ${summarizeTriggerConfig(row.trigger_config)}`
                                : ''}
                            </div>
                          </td>
                          <td className="px-3 md:px-4 py-3 font-mono text-xs text-gray-700 hidden md:table-cell">
                            {row.code || '—'}
                          </td>
                          <td className="px-3 md:px-4 py-3 text-gray-700 hidden lg:table-cell">
                            {row.category || '—'}
                          </td>
                          <td className="px-3 md:px-4 py-3 capitalize text-gray-700 hidden sm:table-cell">
                            {row.rarity || '—'}
                          </td>
                          <td className="px-3 md:px-4 py-3 hidden xl:table-cell">
                            <div className="text-xs text-gray-800 whitespace-nowrap">
                              {row.trigger_type || '—'}
                            </div>
                            <div className="text-[11px] text-gray-400">
                              {summarizeTriggerConfig(row.trigger_config)}
                            </div>
                          </td>
                          <td className="px-3 md:px-4 py-3">
                            <div className="flex flex-wrap gap-1">
                              <StatusPill active={row.is_active !== false} />
                              {deferred ? <DeferredPill /> : null}
                            </div>
                          </td>
                          <td className="px-3 md:px-4 py-3 tabular-nums text-gray-700 hidden xl:table-cell">
                            {row.order ?? '—'}
                          </td>
                          <td className="px-3 md:px-4 py-3 text-gray-700 hidden xl:table-cell">
                            {row.show_progress ? 'Yes' : 'No'}
                          </td>
                          <td className="px-3 md:px-4 py-3 text-right">
                            <div className="inline-flex items-center justify-end gap-2">
                              <button
                                type="button"
                                onClick={() => openDetail(row._id)}
                                className="p-1.5 rounded-lg transition-colors hover:opacity-90"
                                style={{ backgroundColor: '#F2E2F7' }}
                                title="Details"
                                aria-label="View badge details"
                              >
                                <HiEye className="w-4 h-4" style={{ color: '#58327E' }} />
                              </button>
                              <button
                                type="button"
                                onClick={() => openEdit(row)}
                                className="p-1.5 rounded-lg hover:opacity-90"
                                style={{ backgroundColor: '#F2E2F7' }}
                                title="Edit"
                                aria-label="Edit badge"
                              >
                                <MdEdit className="w-4 h-4" style={{ color: '#58327E' }} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {totalCount > 0 && (
                <div className="px-4 md:px-6 py-3 md:py-4 flex justify-center sm:justify-end border-t border-gray-200 flex-shrink-0">
                  <div className="flex items-center gap-1 flex-wrap justify-center">
                    <button
                      type="button"
                      onClick={() => changePage((p) => Math.max(p - 1, 1))}
                      disabled={currentPage === 1}
                      className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40"
                      aria-label="Previous page"
                    >
                      <HiChevronLeft className="w-4 h-4" />
                    </button>
                    {getPaginationRange().map((page, idx) =>
                      page === 'dots' ? (
                        <span key={`dots-${idx}`} className="px-2 text-sm text-gray-500">
                          …
                        </span>
                      ) : (
                        <button
                          type="button"
                          key={page}
                          onClick={() => changePage(page)}
                          className={`min-w-[30px] h-8 px-2 rounded-lg text-sm font-medium ${
                            page === currentPage
                              ? 'bg-brand text-white shadow-sm'
                              : 'text-gray-900 hover:bg-gray-100'
                          }`}
                        >
                          {page}
                        </button>
                      )
                    )}
                    <button
                      type="button"
                      onClick={() => changePage((p) => Math.min(p + 1, totalPages))}
                      disabled={currentPage >= totalPages}
                      className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40"
                      aria-label="Next page"
                    >
                      <HiChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {detailOpen && (
        <div
          className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="meditation-achievement-detail-title"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeDetail();
          }}
        >
          <div className="w-full sm:max-w-2xl max-h-[90vh] overflow-hidden bg-white rounded-t-2xl sm:rounded-2xl shadow-xl flex flex-col">
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-100">
              <h2
                id="meditation-achievement-detail-title"
                className="text-base font-semibold text-gray-900"
              >
                Badge details
              </h2>
              <button
                type="button"
                onClick={closeDetail}
                className="p-2 rounded-lg text-gray-500 hover:bg-gray-100"
                aria-label="Close details"
              >
                <HiX className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-4">
              {detailLoading ? (
                <p className="text-sm text-gray-500" role="status">
                  Loading…
                </p>
              ) : detailError ? (
                <div>
                  <p className="text-sm text-red-600 whitespace-pre-line mb-3">{detailError}</p>
                  <button
                    type="button"
                    onClick={closeDetail}
                    className="px-3 py-1.5 text-sm font-medium border border-gray-300 rounded-lg"
                  >
                    Close
                  </button>
                </div>
              ) : detailDoc ? (
                <dl>
                  <DetailRow label="Badge #">{detailDoc.badge_number ?? '—'}</DetailRow>
                  <DetailRow label="Name">{detailDoc.name || '—'}</DetailRow>
                  <DetailRow label="Sanskrit / alternate">
                    {detailDoc.sanskrit_name || '—'}
                  </DetailRow>
                  <DetailRow label="Code">
                    <code className="text-xs">{detailDoc.code || '—'}</code>
                  </DetailRow>
                  <DetailRow label="Description">{detailDoc.description || '—'}</DetailRow>
                  <DetailRow label="Category">{detailDoc.category || '—'}</DetailRow>
                  <DetailRow label="Rarity / tier">
                    <span className="capitalize">{detailDoc.rarity || '—'}</span>
                  </DetailRow>
                  <DetailRow label="Trigger type">{detailDoc.trigger_type || '—'}</DetailRow>
                  <DetailRow label="Trigger config">
                    <pre className="text-xs bg-gray-50 border border-gray-100 rounded-lg p-2 overflow-x-auto whitespace-pre-wrap">
                      {formatTriggerConfig(detailDoc.trigger_config)}
                    </pre>
                  </DetailRow>
                  <DetailRow label="Logo">
                    {detailDoc.logo_url ? (
                      <div className="flex items-center gap-3">
                        <img
                          src={detailDoc.logo_url}
                          alt=""
                          className="h-16 w-16 rounded-lg object-cover border border-gray-200"
                        />
                        <a
                          href={detailDoc.logo_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs text-brand break-all hover:underline"
                        >
                          {detailDoc.logo_url}
                        </a>
                      </div>
                    ) : (
                      '—'
                    )}
                  </DetailRow>
                  <DetailRow label="Order">{detailDoc.order ?? '—'}</DetailRow>
                  <DetailRow label="Active">
                    <StatusPill active={detailDoc.is_active !== false} />
                  </DetailRow>
                  <DetailRow label="Deferred">
                    {detailDoc.deferred === true ? <DeferredPill /> : 'No'}
                  </DetailRow>
                  <DetailRow label="Show progress">
                    {detailDoc.show_progress ? 'Yes' : 'No'}
                  </DetailRow>
                  <DetailRow label="ID">
                    <code className="text-xs break-all">{detailDoc._id}</code>
                  </DetailRow>
                </dl>
              ) : null}
            </div>
            <div className="px-4 py-3 border-t border-gray-100 flex justify-end gap-2">
              {detailDoc ? (
                <button
                  type="button"
                  onClick={() => {
                    openEdit(detailDoc);
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-brand rounded-lg hover:opacity-90"
                >
                  Edit badge
                </button>
              ) : null}
              <button
                type="button"
                onClick={closeDetail}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <BadgeFormModal
        open={formOpen}
        mode={formMode}
        initialDoc={formDoc}
        enums={enums}
        busy={formSaving}
        generalError={formError}
        onClose={() => {
          if (formSaving) return;
          setFormOpen(false);
          setFormError('');
        }}
        onSubmit={handleFormSubmit}
      />
    </div>
  );
};

export default MeditationAchievements;
